/**
 * WebhookRuleTestDialog - Modal para testar webhook de roteamento
 * 
 * Permite enviar um payload de teste para o webhook e ver o resultado.
 * 
 * @version 1.0.0
 * @date 2026-01-30
 */

import { useState } from 'react';
import { Play, CheckCircle2, XCircle, Clock, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useRoutingWebhookLogs } from '@/hooks/useRoutingWebhookLogs';

// ============= INTERFACES =============

interface WebhookRuleTestDialogProps {
  /** Se o modal está aberto */
  open: boolean;
  /** Callback para fechar o modal */
  onOpenChange: (open: boolean) => void;
  /** Token do webhook */
  webhookToken: string;
  /** Mapeamento de campos para gerar payload de exemplo */
  fieldMapping?: Record<string, string>;
}

// ============= PAYLOAD DE EXEMPLO =============

const getDefaultPayload = (fieldMapping?: Record<string, string>) => {
  const phoneField = fieldMapping?.contact_phone || 'telefone';
  const nameField = fieldMapping?.contact_name || 'nome';

  // Gerar payload baseado no mapeamento
  const payload: Record<string, unknown> = {};

  // Processar campo de telefone (pode ter dot notation)
  if (phoneField.includes('.')) {
    const parts = phoneField.split('.');
    let current: Record<string, unknown> = payload;
    for (let i = 0; i < parts.length - 1; i++) {
      current[parts[i]] = current[parts[i]] || {};
      current = current[parts[i]] as Record<string, unknown>;
    }
    current[parts[parts.length - 1]] = '5511999999999';
  } else {
    payload[phoneField] = '5511999999999';
  }

  // Processar campo de nome (pode ter dot notation)
  if (nameField.includes('.')) {
    const parts = nameField.split('.');
    let current: Record<string, unknown> = payload;
    for (let i = 0; i < parts.length - 1; i++) {
      current[parts[i]] = current[parts[i]] || {};
      current = current[parts[i]] as Record<string, unknown>;
    }
    current[parts[parts.length - 1]] = 'Teste Webhook';
  } else {
    payload[nameField] = 'Teste Webhook';
  }

  return JSON.stringify(payload, null, 2);
};

// ============= COMPONENTE PRINCIPAL =============

export function WebhookRuleTestDialog({
  open,
  onOpenChange,
  webhookToken,
  fieldMapping,
}: WebhookRuleTestDialogProps) {
  const { testWebhook, isTesting } = useRoutingWebhookLogs();

  // Estados locais
  const [payload, setPayload] = useState(getDefaultPayload(fieldMapping));
  const [result, setResult] = useState<{
    success: boolean;
    status: number;
    data: unknown;
    time: number;
  } | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Validar JSON ao digitar
  const handlePayloadChange = (value: string) => {
    setPayload(value);
    setJsonError(null);
    
    try {
      JSON.parse(value);
    } catch (e) {
      setJsonError('JSON inválido');
    }
  };

  // Executar teste
  const handleTest = async () => {
    try {
      const parsedPayload = JSON.parse(payload);
      const testResult = await testWebhook(webhookToken, parsedPayload);
      setResult(testResult);
    } catch (e) {
      setJsonError('JSON inválido');
    }
  };

  // Resetar ao fechar
  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setResult(null);
      setPayload(getDefaultPayload(fieldMapping));
      setJsonError(null);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Testar Webhook
          </DialogTitle>
          <DialogDescription>
            Envie um payload de teste para verificar se o webhook está funcionando corretamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1">
          {/* Textarea do payload */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Payload JSON</label>
            <Textarea
              value={payload}
              onChange={(e) => handlePayloadChange(e.target.value)}
              className="font-mono text-xs min-h-[150px]"
              placeholder="Digite o payload JSON..."
            />
            {jsonError && (
              <p className="text-xs text-red-500">{jsonError}</p>
            )}
          </div>

          {/* Resultado do teste */}
          {result && (
            <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
              {/* Status */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {result.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <span className="font-medium">
                    {result.success ? 'Sucesso' : 'Erro'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">
                    HTTP {result.status}
                  </Badge>
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {result.time}ms
                  </Badge>
                </div>
              </div>

              {/* Resposta */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase">
                  Resposta
                </p>
                <pre className="text-xs bg-background rounded-lg p-3 overflow-auto max-h-24 border break-all whitespace-pre-wrap">
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Fechar
          </Button>
          <Button 
            onClick={handleTest}
            disabled={isTesting || !!jsonError}
          >
            {isTesting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Testando...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Enviar Teste
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
